/**
 * One screen for all three modes. It renders a View and animates from events;
 * it never touches the engine. Coach mode adds a panel driven by shared/coach.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type Card, label, longName, sortCards } from '@shared/cards';
import type { GameEvent, Seat } from '@shared/engine';
import type { RoomInfo } from '@shared/protocol';
import type { View } from '@shared/view';
import { type Score, type ScoreItem, itemLabel, scoreHand } from '@shared/scoring';
import { type Position, mode, parDelta } from '@shared/ai';
import {
  type CountReview,
  type DiscardReview,
  type PegReview,
  TIPS,
  discardHint,
  pegHint,
  reviewCount,
  reviewDiscard,
  reviewPeg,
  speakScore,
} from '@shared/coach';
import type { ConnStatus, GameAdapter } from './adapter';
import { CardFace } from './components/CardFace';
import { Board } from './components/Board';
import { CountPad } from './components/CountPad';
import { Sheet } from './components/Sheet';
import { type HandRecord, type SessionState, type Tier, type TierState, TIER_NAME, afterHand, loadTier, newSession, pegThreshold, resetTierProgress, saveSession, saveTier, summarize } from './coachState';

interface Callout {
  id: number;
  text: string;
  who: 'you' | 'opp' | 'neutral';
}

interface Miss {
  phase: 'discard' | 'pegging' | 'count';
  gap: number;
  yourPlay: string;
  bestPlay: string;
  principle: string;
}

type CoachContent =
  | { kind: 'discard'; review: DiscardReview }
  | { kind: 'peg'; review: PegReview }
  | { kind: 'hint'; text: string }
  | { kind: 'count'; review: CountReview }
  | { kind: 'note'; text: string };

const CALLOUT_MS = 1200;
/** Keep at most this many callouts waiting; older ones are dropped, newest kept. */
const CALLOUT_MAX_QUEUE = 3;

/** "6 ahead · play off" for the one-line top row; the full sentence lives in the coach. */
function parShort(pos: Position): string {
  const m = mode(pos);
  const d = Math.round(parDelta(pos));
  if (m === 'neutral') return 'on par';
  return `${Math.abs(d)} ${d > 0 ? 'ahead' : 'behind'} · play ${m}`;
}

const poss = (name: string) => (name === 'You' ? 'Your' : `${name}'s`);

function scoreCallout(e: Extract<GameEvent, { t: 'score' }>): string {
  const pts = e.to - e.from;
  switch (e.source) {
    case 'heels':
      return `His heels · 2`;
    case 'go':
      return `Go · 1`;
    case 'lastcard':
      return `Last card · 1`;
    case 'show-pone':
    case 'show-dealer':
      return `Hand · ${pts}`;
    case 'show-crib':
      return `Crib · ${pts}`;
    case 'peg': {
      const parts = e.items.map((i) => (i.kind === 'fifteen' ? 'Fifteen' : i.kind === 'thirtyone' ? 'Thirty-one' : itemLabel(i)));
      return `${parts.join(' + ')} · ${pts}`;
    }
  }
}

export function GameScreen(props: { adapter: GameAdapter; onExit: () => void; countMode: boolean }) {
  const { adapter } = props;
  const coachMode = adapter.mode === 'coach';
  const [view, setView] = useState<View | null>(adapter.current());
  const [status, setStatus] = useState<ConnStatus>({ k: adapter.mode === 'online' ? 'connecting' : 'local' });
  const [room, setRoom] = useState<RoomInfo | null>(null);
  const [selected, setSelected] = useState<Card[]>([]);
  const [pending, setPending] = useState(false);
  const [callouts, setCallouts] = useState<Callout[]>([]);
  const [live, setLive] = useState('');
  const [prevScores, setPrevScores] = useState<[number, number]>([0, 0]);
  const [highlight, setHighlight] = useState<Card[]>([]);
  const [activeItem, setActiveItem] = useState<number | null>(null);
  const [countEntered, setCountEntered] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [now, setNow] = useState(Date.now());

  // Coach state
  const [coachOpen, setCoachOpen] = useState(true);
  const [coach, setCoach] = useState<CoachContent | null>(null);
  const [tierState, setTierState] = useState<TierState>(() => resetTierProgress(loadTier()));
  const tierStart = useRef<Tier>(tierState.tier);
  const [session, setSession] = useState<SessionState>(() => (coachMode ? newSession() : { startedAt: new Date().toISOString(), hands: [] }));
  const [sessionMiss, setSessionMiss] = useState<Miss | null>(null);
  const [missedItems, setMissedItems] = useState<ScoreItem[]>([]);
  const [hintLevel, setHintLevel] = useState<0 | 1 | 2 | 3>(0);
  const [lastMiss, setLastMiss] = useState<Miss | { clean: true; best: string } | null>(null);
  const dealt = useRef<Card[]>([]);
  const handLog = useRef<{ discardLoss: number; pegLeft: number; countEntered: number | null; countTrue: number; hintMax: 0 | 1 | 2 | 3; misses: Miss[]; pegMissOver1: boolean; bestDecision: string }>(freshLog());
  const calloutId = useRef(0);
  /** A show score held back until the player has entered their count. */
  const pendingShowScore = useRef<Extract<GameEvent, { t: 'score' }> | null>(null);

  function freshLog() {
    return { discardLoss: 0, pegLeft: 0, countEntered: null as number | null, countTrue: 0, hintMax: 0 as 0 | 1 | 2 | 3, misses: [] as Miss[], pegMissOver1: false, bestDecision: '' };
  }

  const you: Seat = view?.you ?? 0;
  const opp: Seat = you === 0 ? 1 : 0;
  const names: [string, string] = useMemo(() => {
    if (adapter.mode === 'online' && room) {
      const n: [string, string] = [room.names[0] || 'Host', room.names[1] || 'Guest'];
      n[you] = 'You';
      return n;
    }
    return you === 0 ? ['You', 'Computer'] : ['Computer', 'You'];
  }, [adapter.mode, room, you]);

  const pushCallout = useCallback((text: string, who: Callout['who']) => {
    calloutId.current += 1;
    setCallouts((q) => {
      const next = [...q, { id: calloutId.current, text, who }];
      // The one on screen stays; drop the oldest waiting ones beyond the cap.
      while (next.length > CALLOUT_MAX_QUEUE) next.splice(1, 1);
      return next;
    });
  }, []);

  // Callout queue: one at a time, ≥ 1.2 s each.
  const frontId = callouts[0]?.id;
  useEffect(() => {
    if (frontId === undefined) return;
    const t = setTimeout(() => setCallouts((q) => (q[0]?.id === frontId ? q.slice(1) : q)), CALLOUT_MS);
    return () => clearTimeout(t);
  }, [frontId]);

  // A new hand clears any selection, even if the 'dealt' event was missed
  // across a reconnect.
  const handNo = view?.handNo;
  useEffect(() => {
    setSelected([]);
  }, [handNo]);

  // Presence clock for the "end game" offer.
  useEffect(() => {
    if (adapter.mode !== 'online') return;
    const t = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(t);
  }, [adapter.mode]);

  // Subscribe to the adapter.
  useEffect(() => {
    const offView = adapter.onView((v, events) => {
      setView((prev) => {
        if (prev) setPrevScores(prev.scores);
        return v;
      });
      setPending(false);
      handleEvents(v, events);
    });
    const offStatus = adapter.onStatus((s) => {
      setStatus(s);
      // A resume that found no table: back to Home without a dead-end screen.
      if (s.k === 'closed' && s.reason === 'That table is gone.') props.onExit();
    });
    const offRoom = adapter.onRoom(setRoom);
    const offErr = adapter.onError((_code, message) => {
      setError(message);
      setPending(false);
      setTimeout(() => setError(null), 2500);
    });
    // The local driver emits its first view in a microtask, before this effect
    // subscribes. Catch up from the current view so the coach learns the deal.
    const cur = adapter.current();
    if (cur) {
      setView(cur);
      handleEvents(cur, []);
    }
    return () => {
      offView();
      offStatus();
      offRoom();
      offErr();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter]);

  function handleEvents(v: View, events: GameEvent[]) {
    const me = v.you;
    const sentences: string[] = [];
    for (const e of events) {
      switch (e.t) {
        case 'cut':
          break;
        case 'cut-tie':
          pushCallout('Tie — cut again', 'neutral');
          break;
        case 'dealer': {
          const c0 = v.cutCards[0];
          const c1 = v.cutCards[1];
          if (c0 !== null && c1 !== null) pushCallout(`Cut ${label(me === 0 ? c0 : c1)} v ${label(me === 0 ? c1 : c0)} · ${e.seat === me ? 'you deal' : 'they deal'}`, 'neutral');
          sentences.push(`${e.seat === me ? 'You' : 'Opponent'} deal${e.seat === me ? '' : 's'} first.`);
          break;
        }
        case 'dealt':
          sentences.push(`Hand ${e.handNo}. ${e.dealer === me ? 'You deal' : 'Opponent deals'}.`);
          setSelected([]);
          setCountEntered(null);
          setHighlight([]);
          setActiveItem(null);
          setHintLevel(0);
          if (coachMode) setCoach(null);
          break;
        case 'discarded':
          if (e.seat !== me) sentences.push('Opponent has discarded.');
          break;
        case 'starter':
          pushCallout(`Starter ${label(e.card)}`, 'neutral');
          sentences.push(`Starter ${longName(e.card)}.`);
          break;
        case 'played':
          if (e.seat !== me) sentences.push(`Opponent played ${longName(e.card)}. Count ${e.count}.`);
          else sentences.push(`Count ${e.count}.`);
          break;
        case 'go':
          pushCallout('Go', e.seat === me ? 'you' : 'opp');
          sentences.push(`${e.seat === me ? 'You say' : 'Opponent says'} go.`);
          break;
        case 'reset':
          sentences.push('Count resets.');
          break;
        case 'score': {
          // In Coach mode the player counts their own hand/crib first: the
          // callout and the spoken total would give the answer away.
          const isShowStep = e.source === 'show-pone' || e.source === 'show-dealer' || e.source === 'show-crib';
          if (coachMode && props.countMode && isShowStep && e.seat === me) {
            pendingShowScore.current = e;
            break;
          }
          pushCallout(scoreCallout(e), e.seat === me ? 'you' : 'opp');
          const pts = e.to - e.from;
          const what = e.source === 'peg' ? e.items.map((i) => itemLabel(i).toLowerCase()).join(' and ') : e.source === 'heels' ? 'his heels' : e.source === 'go' ? 'the go' : e.source === 'lastcard' ? 'last card' : e.source === 'show-crib' ? 'the crib' : 'the hand';
          const otherSeat = e.seat === 0 ? 1 : 0;
          sentences.push(`${e.seat === me ? 'You score' : 'Opponent scores'} ${pts} for ${what}. ${e.seat === me ? 'You' : 'Opponent'} ${e.to}, ${otherSeat === me ? 'you' : 'opponent'} ${v.scores[otherSeat]}.`);
          break;
        }
        case 'show':
          setCountEntered(null);
          setHighlight([]);
          setActiveItem(null);
          setMissedItems([]);
          setCallouts([]);
          if (coachMode) setCoach((c) => (c && (c.kind === 'peg' || c.kind === 'hint') ? null : c));
          sentences.push(`Counting ${e.step === 'crib' ? 'the crib' : e.seat === me ? 'your hand' : "opponent's hand"}.`);
          break;
        case 'hand-end':
          setCallouts([]);
          onHandEnd(v);
          break;
        case 'game-over':
          sentences.push(e.winner === me ? 'You win.' : 'Opponent wins.');
          break;
      }
    }
    // A zero-width space toggles so an identical sentence still re-announces.
    if (sentences.length) setLive((prev) => sentences.join(' ') + (prev.endsWith('\u200B') ? '' : '\u200B'));
    // Remember the six you were dealt (for the coach's "seen" set).
    if (v.phase === 'discard' && v.hand.length === 6 && !v.submitted[me]) dealt.current = [...v.hand];
  }

  // ── coach bookkeeping ──

  function position(v: View): Position {
    return { myScore: v.scores[v.you], oppScore: v.scores[v.you === 0 ? 1 : 0], isDealer: v.dealer === v.you, handNo: v.handNo };
  }

  function seenCards(v: View): Card[] {
    const s = new Set<Card>(dealt.current);
    if (v.starter !== null) s.add(v.starter);
    if (v.peg) for (const side of v.peg.played) for (const c of side) s.add(c);
    return [...s];
  }

  function myShowScore(v: View): number | undefined {
    if (v.starter === null || !v.peg) return undefined;
    const kept = [...v.hand, ...v.peg.played[v.you]];
    if (kept.length !== 4) return undefined;
    return scoreHand(kept, v.starter, false).total;
  }

  function onHandEnd(v: View) {
    if (!coachMode) return;
    const log = handLog.current;
    const rec: HandRecord = {
      n: v.handNo,
      role: v.dealer === v.you ? 'dealer' : 'pone',
      discardLoss: log.discardLoss,
      pegLeft: log.pegLeft,
      countEntered: log.countEntered,
      countTrue: log.countTrue,
      hintLevel: log.hintMax,
    };
    const s = { ...session, hands: [...session.hands, rec] };
    setSession(s);
    saveSession(s);
    const t = afterHand(tierState, rec, log.pegMissOver1);
    if (t.tier !== tierState.tier) pushCallout(`Coach: ${TIER_NAME[t.tier]}`, 'neutral');
    setTierState(t);
    saveTier(t);
    const big = log.misses.filter((m) => m.gap > 0.3).sort((a, b) => b.gap - a.gap)[0];
    setLastMiss(big ?? { clean: true, best: log.bestDecision || 'Every decision was within a third of a point of the best.' });
    if (big) setSessionMiss((m) => (!m || big.gap > m.gap ? big : m));
    handLog.current = freshLog();
  }

  // ── actions ──

  function act(body: Parameters<GameAdapter['dispatch']>[0]) {
    setPending(adapter.mode === 'online');
    adapter.dispatch(body);
  }

  function tapCard(c: Card) {
    if (!view) return;
    if (view.phase === 'discard') {
      if (view.submitted[you]) return;
      setSelected((s) => (s.includes(c) ? s.filter((x) => x !== c) : s.length >= 2 ? [s[1]!, c] : [...s, c]));
      return;
    }
    if (view.phase === 'pegging' && view.peg && view.peg.turn === you && !pending) {
      if (!view.peg.legal.includes(c)) return;
      if (selected[0] === c) {
        playCard(c);
        return;
      }
      setSelected([c]);
    }
  }

  function commitDiscard() {
    if (!view || selected.length !== 2) return;
    if (coachMode && dealt.current.length === 6) {
      const review = reviewDiscard(dealt.current, selected, position(view));
      handLog.current.discardLoss = review.gap;
      if (review.gap > 0.3) handLog.current.misses.push({ phase: 'discard', gap: review.gap, yourPlay: `kept ${review.chosen.keep.map(label).join(' ')}`, bestPlay: `keep ${review.best.keep.map(label).join(' ')}`, principle: review.principle });
      else handLog.current.bestDecision = review.rank === 1 ? `Your keep (${review.chosen.keep.map(label).join(' ')}) was the best of 15.` : `Your discard was within ${review.gap.toFixed(1)} of the best.`;
      if (tierState.tier === 1 || (tierState.tier === 2 && review.gap > 1.0)) {
        setCoach({ kind: 'discard', review });
        setCoachOpen(true);
        setTimeout(() => setLive(`${review.headline} ${review.explanation}`), 1000);
      } else setCoach(null);
    }
    act({ t: 'discard', cards: [selected[0]!, selected[1]!] });
    setSelected([]);
    setHintLevel(0);
  }

  function playCard(c: Card) {
    if (!view || !view.peg) return;
    if (coachMode) {
      const ctx = { hand: view.hand, stack: view.peg.stack, seen: seenCards(view), oppCards: view.oppCount, pos: position(view), myShowScore: myShowScore(view) };
      const review = reviewPeg(ctx, c, pegThreshold(tierState.tier));
      handLog.current.pegLeft += Math.max(0, review.loss);
      if (review.loss > 1.0) handLog.current.pegMissOver1 = true;
      if (review.loss > 0.3) handLog.current.misses.push({ phase: 'pegging', gap: review.loss, yourPlay: `played ${label(c)} to ${review.chosen.countAfter}`, bestPlay: `play ${label(review.best.card)} to ${review.best.countAfter}`, principle: review.principle ?? review.line ?? '' });
      if (review.line) {
        setCoach({ kind: 'peg', review });
        setCoachOpen(true);
        setTimeout(() => setLive(review.line ?? ''), 1000);
      } else if (coach?.kind === 'peg' || coach?.kind === 'hint') setCoach(null);
    }
    act({ t: 'play', card: c });
    setSelected([]);
    setHintLevel(0);
  }

  function hint() {
    if (!view) return;
    const level = Math.min(3, hintLevel + 1) as 1 | 2 | 3;
    setHintLevel(level);
    handLog.current.hintMax = Math.max(handLog.current.hintMax, level) as 0 | 1 | 2 | 3;
    let text = '';
    if (view.phase === 'discard' && dealt.current.length === 6) text = discardHint(dealt.current, position(view), level);
    else if (view.phase === 'pegging' && view.peg) text = pegHint({ hand: view.hand, stack: view.peg.stack, seen: seenCards(view), oppCards: view.oppCount, pos: position(view), myShowScore: myShowScore(view) }, level);
    if (text) {
      setCoach({ kind: 'hint', text: `Hint ${level}/3 — ${text}` });
      setCoachOpen(true);
    }
  }

  function submitCount(n: number) {
    if (!view?.show) return;
    const review = reviewCount(n, view.show.score);
    setCountEntered(n);
    handLog.current.countEntered = n;
    handLog.current.countTrue = view.show.score.total;
    if (review.verdict !== 'right') {
      const gap = Math.abs(n - view.show.score.total);
      handLog.current.misses.push({ phase: 'count', gap, yourPlay: `counted ${n}`, bestPlay: `${view.show.score.total}`, principle: TIPS.counting });
    } else if (!handLog.current.bestDecision) handLog.current.bestDecision = `You counted ${view.show.score.total} exactly.`;
    setHighlight(review.missed.flatMap((m) => m.cards));
    setMissedItems(review.missed);
    setCoach({ kind: 'count', review });
    setCoachOpen(true);
    const held = pendingShowScore.current;
    pendingShowScore.current = null;
    if (held) pushCallout(scoreCallout(held), 'you');
    setLive(`${review.line} ${speakScore(view.show.score)}`);
  }

  function toggleItem(i: number, it: ScoreItem) {
    if (activeItem === i) {
      setActiveItem(null);
      setHighlight([]);
    } else {
      setActiveItem(i);
      setHighlight(it.cards);
    }
  }

  function cycleTier() {
    const next = ((tierState.tier % 3) + 1) as Tier;
    const t = { ...tierState, tier: next, override: true };
    setTierState(t);
    saveTier(t);
  }

  // ── render pieces ──

  if (!view) {
    return (
      <div className="screen game">
        <div className="toprow">
          <button type="button" className="nav-back" onClick={() => exit()} aria-label="Back to home">
            ‹ Home
          </button>
          <div className="mid" />
          <span className="conn">{status.k === 'open' ? '● online' : status.k === 'connecting' ? '◌ connecting' : status.k === 'reconnecting' ? '◌ reconnecting' : ''}</span>
        </div>
        <div className="table">
          {status.k === 'closed' ? <div className="msg">{status.reason}</div> : room?.waiting ? <Waiting room={room} /> : <div className="msg muted">Connecting…</div>}
        </div>
        <div className="actions">
          <div className="spacer" />
        </div>
      </div>
    );
  }

  const v = view;
  const isDealer = v.dealer === you;
  const myTurn = v.phase === 'pegging' && v.peg?.turn === you && !pending;
  const peg = v.peg;
  const sixUp = v.phase === 'discard' && v.hand.length === 6;

  function exit() {
    if (adapter.mode === 'online') adapter.leave();
    props.onExit();
  }

  // Primary action
  let primary: { label: string; onClick: () => void; disabled?: boolean } | null = null;
  switch (v.phase) {
    case 'discard':
      if (!v.submitted[you]) primary = { label: 'Put in crib', onClick: commitDiscard, disabled: selected.length !== 2 || pending };
      break;
    case 'cut-starter':
      if (!isDealer) primary = { label: 'Cut the deck', onClick: () => act({ t: 'cut' }), disabled: pending };
      break;
    case 'pegging':
      if (peg && peg.turn === you) {
        if (peg.mustGo) primary = { label: 'Go', onClick: () => act({ t: 'go' }), disabled: pending };
        else primary = { label: 'Play', onClick: () => selected[0] !== undefined && playCard(selected[0]), disabled: pending || selected.length !== 1 || !peg.legal.includes(selected[0]!) };
      }
      break;
    case 'show': {
      const mine = v.show!.seat === you;
      const needCount = coachMode && props.countMode && mine && countEntered === null;
      if (!v.acked[you]) primary = { label: 'Continue', onClick: () => act({ t: 'ack' }), disabled: pending || needCount };
      break;
    }
    case 'hand-end':
      if (!v.acked[you]) primary = { label: 'Next hand', onClick: () => act({ t: 'ack' }), disabled: pending };
      break;
    case 'game-over':
      primary = { label: adapter.mode === 'online' ? (room?.rematch[you] ? 'Waiting…' : 'Rematch') : 'Play again', onClick: () => adapter.newGame(), disabled: adapter.mode === 'online' && !!room?.rematch[you] };
      break;
  }

  const hintAvailable = coachMode && ((v.phase === 'discard' && !v.submitted[you]) || (v.phase === 'pegging' && myTurn && !peg?.mustGo));

  const oppAwayFor = room?.opp === 'away' && room.oppAwaySince ? now - room.oppAwaySince : 0;

  return (
    <div className="screen game">
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {live}
      </div>
      <div className="toprow">
        <button type="button" className="nav-back" onClick={exit} aria-label="Back to home">
          ‹ Home
        </button>
        <div className="mid">
          {v.phase === 'game-over' ? '' : `Hand ${v.handNo} · ${poss(names[v.dealer ?? you]).toLowerCase()} crib`}
          {coachMode && v.phase !== 'game-over' ? ` · ${parShort(position(v))}` : ''}
        </div>
        <span className="conn">{status.k === 'open' ? '● online' : status.k === 'reconnecting' ? '◌ reconnecting' : status.k === 'connecting' ? '◌ connecting' : ''}</span>
      </div>
      <Board you={you} names={names} scores={v.scores} prev={prevScores} dealer={v.dealer} />

      {room && room.opp === 'away' && v.phase !== 'game-over' ? (
        <div className="banner warn">
          <span>{names[opp]} is reconnecting…</span>
          {oppAwayFor > 3 * 60_000 ? (
            <button type="button" className="btn danger" onClick={() => adapter.endAbandoned()}>
              End game
            </button>
          ) : null}
        </div>
      ) : null}
      {status.k === 'reconnecting' ? <div className="banner warn">Reconnecting…</div> : null}
      {error ? <div className="banner warn">{error}</div> : null}

      {coachMode ? (
        <CoachPanel
          open={coachOpen}
          setOpen={setCoachOpen}
          content={coach}
          tier={tierState.tier}
          onTier={cycleTier}
          onShowAll={() => setShowAll(true)}
          onMore={() => setMoreOpen(true)}
          counting={v.phase === 'show' && !!v.show && v.show.seat === you && props.countMode && countEntered === null}
        />
      ) : null}

      <div className="table" data-phase={v.phase}>
        {/* opponent's hand and the fixed piles */}
        {(v.phase === 'discard' || v.phase === 'cut-starter' || v.phase === 'pegging') && (
          <div className="opp-hand" aria-hidden="true">
            {Array.from({ length: v.oppCount }, (_, i) => (
              <CardFace key={i} faceDown size="small" />
            ))}
          </div>
        )}
        {v.phase !== 'discard' && v.phase !== 'game-over' && v.phase !== 'hand-end' && v.cribCount > 0 && v.phase !== 'show' ? (
          <div className="crib-pile" aria-hidden="true">
            <CardFace faceDown size="small" />
            <span>crib</span>
          </div>
        ) : null}
        {v.starter !== null && v.phase === 'pegging' ? (
          <div className="starter">
            <CardFace card={v.starter} size="small" label={`Starter, ${longName(v.starter)}`} highlight={highlight.includes(v.starter)} />
            <span>starter</span>
          </div>
        ) : null}

        {callouts[0] ? (
          <div key={callouts[0].id} className={`callout ${callouts[0].who}`} aria-hidden="true">
            {callouts[0].text}
          </div>
        ) : null}

        {v.phase === 'discard' ? (
          v.submitted[you] ? (
            <div className="msg muted">Waiting for {names[opp]} to discard…</div>
          ) : (
            <div className="msg">
              Choose two for <b>{poss(names[v.dealer ?? you]).toLowerCase()}</b> crib
              <div className="msg muted">{selected.length ? `${selected.length} of 2 chosen` : ''}{v.submitted[opp] ? ` · ${names[opp]} has discarded` : ''}</div>
            </div>
          )
        ) : null}

        {v.phase === 'cut-starter' ? <div className="msg">{isDealer ? `${names[opp]} cuts the deck…` : 'Cut for the starter'}</div> : null}

        {v.phase === 'pegging' && peg ? (
          <>
            <div className="count" aria-hidden="true">
              {peg.count}
            </div>
            <div className="count-label" aria-hidden="true">
              {peg.turn === you ? (peg.mustGo ? 'no card fits — say Go' : 'your play') : `${names[opp]} to play`}
            </div>
            <div className="stack">
              {peg.stack.map((c) => (
                <CardFace key={c} card={c} size="table" dim={false} />
              ))}
            </div>
            {peg.played[0].length + peg.played[1].length > peg.stack.length ? (
              <div className="played-strip" aria-label="played earlier">
                {[...peg.played[0], ...peg.played[1]].filter((c) => !peg.stack.includes(c)).map((c) => (
                  <CardFace key={c} card={c} size="small" dim />
                ))}
              </div>
            ) : null}
          </>
        ) : null}

        {v.phase === 'show' && v.show ? (
          <ShowArea
            v={v}
            names={names}
            coachCount={coachMode && props.countMode}
            countEntered={countEntered}
            onCount={submitCount}
            highlight={highlight}
            activeItem={activeItem}
            toggleItem={toggleItem}
            missed={missedItems}
          />
        ) : null}

        {v.phase === 'hand-end' ? <HandEnd v={v} names={names} miss={coachMode ? lastMiss : null} /> : null}

        {v.phase === 'game-over' ? <GameOver v={v} names={names} session={coachMode ? session : null} room={room} sessionMiss={sessionMiss} tiers={coachMode ? [tierStart.current, tierState.tier] : null} /> : null}
      </div>

      <div className={`hand${sixUp ? ' six' : ''}`} role="group" aria-label="Your hand">
        {v.phase === 'show' && v.show && v.show.seat === you
          ? sortCards(v.show.cards).map((c) => <CardFace key={c} card={c} highlight={highlight.includes(c)} />)
          : null}
        {sortCards(v.hand).map((c) => {
          const legal = v.phase === 'discard' ? !v.submitted[you] : v.phase === 'pegging' ? myTurn && (peg?.legal.includes(c) ?? false) : false;
          return (
            <CardFace
              key={c}
              card={c}
              selected={selected.includes(c)}
              disabled={!legal}
              highlight={highlight.includes(c)}
              onClick={() => tapCard(c)}
              label={`${longName(c)}${selected.includes(c) ? ', selected' : ''}${v.phase === 'pegging' && myTurn && !legal ? ', too high' : ''}`}
            />
          );
        })}
      </div>

      <div className="actions">
        {hintAvailable ? (
          <button type="button" className="btn secondary hint" onClick={hint}>
            Hint{hintLevel ? ` ${hintLevel}/3` : ''}
          </button>
        ) : null}
        <div className="spacer" />
        {v.phase === 'game-over' ? (
          <button type="button" className="btn ghost" onClick={exit}>
            Home
          </button>
        ) : null}
        {primary ? (
          <button type="button" className="btn" onClick={primary.onClick} disabled={primary.disabled}>
            {primary.label}
          </button>
        ) : (
          <div className="btn ghost" aria-hidden="true" />
        )}
      </div>

      <Sheet open={moreOpen} title="Coach" onClose={() => setMoreOpen(false)}>
        {coach?.kind === 'discard' ? (
          <>
            <p>{coach.review.headline}</p>
            <p>{coach.review.explanation}</p>
            <p className="principle">{coach.review.principle}</p>
          </>
        ) : coach?.kind === 'count' ? (
          <>
            <p>{coach.review.line}</p>
            <p className="principle">{TIPS.counting}</p>
          </>
        ) : coach?.kind === 'peg' ? (
          <p>{coach.review.line}</p>
        ) : (
          <p>Nothing to read yet.</p>
        )}
      </Sheet>
      <Sheet open={showAll} title="All 15 keeps" onClose={() => setShowAll(false)}>
        {coach?.kind === 'discard' ? <AllKeeps review={coach.review} isDealer={isDealer} /> : <p>No discard to show yet.</p>}
      </Sheet>
    </div>
  );
}

function Waiting(props: { room: RoomInfo }) {
  const share = async () => {
    const url = `${location.origin}/join/${props.room.code}`;
    const text = `Join my cribbage table: code ${props.room.code}`;
    try {
      if (navigator.share) await navigator.share({ title: 'Cribbage', text, url });
      else await navigator.clipboard.writeText(`${text} — ${url}`);
    } catch {
      /* dismissed */
    }
  };
  return (
    <div className="show">
      <div className="waiting">Your table code</div>
      <div className="code-big" aria-label={`Table code ${props.room.code.split('').join(' ')}`}>
        {props.room.code}
      </div>
      <div className="waiting">Waiting for a friend to join…</div>
      <button type="button" className="btn secondary" onClick={share}>
        Share code
      </button>
    </div>
  );
}

function CoachPanel(props: { open: boolean; setOpen: (b: boolean) => void; content: CoachContent | null; tier: Tier; onTier: () => void; onShowAll: () => void; onMore: () => void; counting: boolean }) {
  const c = props.content;
  const headline = !c
    ? props.counting
      ? 'Count your hand, then tap ✓.'
      : 'Make your play. The coach speaks after.'
    : c.kind === 'discard'
      ? c.review.headline
      : c.kind === 'peg'
        ? 'Pegging'
        : c.kind === 'count'
          ? c.review.verdict === 'right'
            ? c.review.line
            : `You said ${c.review.entered}; it's ${c.review.truth}.`
          : c.kind === 'hint'
            ? c.text.split(' — ')[0]!
            : c.text;
  return (
    <div className="coach">
      <div className="head">
        <button type="button" className="head" onClick={() => props.setOpen(!props.open)} aria-expanded={props.open}>
          <span className="line">{headline}</span>
          <span aria-hidden="true">{props.open ? '▾' : '▸'}</span>
        </button>
        <button type="button" className="tier" onClick={props.onTier} aria-label={`Coach level ${TIER_NAME[props.tier]}, tap to change`}>
          Coach: {TIER_NAME[props.tier]} ▾
        </button>
      </div>
      {props.open && c ? (
        <div className={`body${c.kind === 'discard' || c.kind === 'count' ? ' long' : ''}`}>
          {c.kind === 'discard' ? (
            <>
              <p>
                <span className={`grade ${c.review.grade}`}>{c.review.grade === 'good' ? 'Good.' : c.review.grade === 'fine' ? 'Fine.' : 'A miss.'}</span> {c.review.explanation}
              </p>
              <p className="principle">{c.review.principle}</p>
              <div className="row">
                <button type="button" className="chip-btn" onClick={props.onShowAll}>
                  Show all 15
                </button>
                <button type="button" className="chip-btn" onClick={props.onMore}>
                  Read in full
                </button>
              </div>
            </>
          ) : c.kind === 'peg' ? (
            <p>{c.review.line}</p>
          ) : c.kind === 'count' ? (
            c.review.verdict === 'right' ? (
              <p>Counted right.</p>
            ) : (
              <>
                <p>{c.review.line}</p>
                <p className="principle">Tap a combination below to see its cards. {TIPS.counting}</p>
              </>
            )
          ) : c.kind === 'hint' ? (
            <p>{c.text.split(' — ').slice(1).join(' — ')}</p>
          ) : (
            <p>{c.text}</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function AllKeeps(props: { review: DiscardReview; isDealer: boolean }) {
  return (
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Keep</th>
          <th>Throw</th>
          <th className="n">Hand</th>
          <th className="n">{props.isDealer ? '+Crib' : '−Crib'}</th>
          <th className="n">Net</th>
        </tr>
      </thead>
      <tbody>
        {props.review.options.map((o, i) => (
          <tr key={i} className={o === props.review.chosen ? 'you' : ''}>
            <td>{i + 1}</td>
            <td>{o.keep.map(label).join(' ')}</td>
            <td>{o.throw.map(label).join(' ')}</td>
            <td className="n">{o.handEV.toFixed(1)}</td>
            <td className="n">{o.cribEV.toFixed(1)}</td>
            <td className="n">{o.total.toFixed(1)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ShowArea(props: {
  v: View;
  names: [string, string];
  coachCount: boolean;
  countEntered: number | null;
  onCount: (n: number) => void;
  highlight: Card[];
  activeItem: number | null;
  toggleItem: (i: number, it: ScoreItem) => void;
  missed: ScoreItem[];
}) {
  const { v } = props;
  const show = v.show!;
  const mine = show.seat === v.you;
  const who = `${poss(props.names[show.seat])} ${show.step === 'crib' ? 'crib' : 'hand'}`;
  const hidden = props.coachCount && mine && props.countEntered === null;
  return (
    <div className={`show${hidden ? ' counting' : ''}`}>
      <div className="who">{who}</div>
      <div className="cards">
        {mine
          ? null
          : sortCards(show.cards).map((c) => <CardFace key={c} card={c} size="table" highlight={props.highlight.includes(c)} />)}
        {!mine ? <span className="plus">+</span> : null}
        <span className="starter-inline">
          <CardFace card={v.starter!} size={hidden ? 'small' : 'table'} highlight={props.highlight.includes(v.starter!)} label={`Starter, ${longName(v.starter!)}`} />
          <span>starter</span>
        </span>
      </div>
      {hidden ? (
        <CountPad prompt="How many points?" onSubmit={props.onCount} />
      ) : (
        <>
          <div className="total">{show.score.total === 0 ? 'Nineteen' : show.score.total}</div>
          <Breakdown score={show.score} active={props.activeItem} toggle={props.toggleItem} missed={props.missed} />
        </>
      )}
    </div>
  );
}

function Breakdown(props: { score: Score; active: number | null; toggle: (i: number, it: ScoreItem) => void; missed: ScoreItem[] }) {
  if (props.score.items.length === 0) return null;
  let running = 0;
  return (
    <div className="breakdown">
      {props.score.items.map((it, i) => {
        running += it.points;
        return (
          <button key={i} type="button" aria-pressed={props.active === i} className={props.missed.some((m) => m.kind === it.kind && m.cards.join() === it.cards.join()) ? 'missed' : ''} onClick={() => props.toggle(i, it)}>
            {itemLabel(it)}
            {it.kind === 'fifteen' || it.kind === 'pair' ? ' ' : ' · '}
            {running}
          </button>
        );
      })}
    </div>
  );
}

function HandEnd(props: { v: View; names: [string, string]; miss: Miss | { clean: true; best: string } | null }) {
  const { v } = props;
  const lh = v.lastHand;
  const d = v.dealer!;
  const p = d === 0 ? 1 : 0;
  const rows = lh
    ? [
        [`${poss(props.names[p])} hand`, scoreHand(lh.kept[p], lh.starter, false).total],
        [`${poss(props.names[d])} hand`, scoreHand(lh.kept[d], lh.starter, false).total],
        [`${poss(props.names[d])} crib`, scoreHand(lh.crib, lh.starter, true).total],
      ]
    : [];
  return (
    <div className="summary">
      <div className="who">Hand {v.handNo} done</div>
      <table>
        <tbody>
          {rows.map(([k, n]) => (
            <tr key={String(k)}>
              <td>{k}</td>
              <td className="n">{n}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {props.miss ? (
        'clean' in props.miss ? (
          <div className="miss-card">
            <b>Clean hand</b>
            {props.miss.best}
          </div>
        ) : (
          <div className="miss-card">
            <b>Biggest miss · {props.miss.phase} · {props.miss.gap.toFixed(1)} pt</b>
            You {props.miss.yourPlay}; better: {props.miss.bestPlay}.<div className="muted">{props.miss.principle}</div>
          </div>
        )
      ) : null}
    </div>
  );
}

function GameOver(props: { v: View; names: [string, string]; session: SessionState | null; room: RoomInfo | null; sessionMiss: Miss | null; tiers: [Tier, Tier] | null }) {
  const { v } = props;
  const won = v.winner === v.you;
  const sum = props.session ? summarize(props.session) : null;
  const f = (x: number) => (Number.isNaN(x) ? '–' : x.toFixed(1));
  return (
    <div className="summary gameover">
      <h2>{won ? 'You win' : `${props.names[v.winner!]} wins`}</h2>
      <div>
        {v.scores[v.you]} – {v.scores[v.you === 0 ? 1 : 0]}
        {v.skunk === 2 ? <span className="skunk"> · double skunk (house)</span> : v.skunk === 1 ? <span className="skunk"> · skunk</span> : null}
      </div>
      {props.room?.rematch[v.you === 0 ? 1 : 0] ? <div className="muted">{props.names[v.you === 0 ? 1 : 0]} wants a rematch.</div> : null}
      {sum && sum.first ? (
        <div className="miss-card">
          <b>This session · {sum.hands} hands</b>
          <table>
            <tbody>
              <tr>
                <td></td>
                <td className="n">first 5</td>
                <td className="n">{sum.last ? 'last 5' : ''}</td>
              </tr>
              <tr>
                <td>Discard loss / hand</td>
                <td className="n">{f(sum.first.discardLoss)}</td>
                <td className="n">{sum.last ? f(sum.last.discardLoss) : ''}</td>
              </tr>
              <tr>
                <td>Peg points left</td>
                <td className="n">{f(sum.first.pegLeft)}</td>
                <td className="n">{sum.last ? f(sum.last.pegLeft) : ''}</td>
              </tr>
              <tr>
                <td>Counted exactly</td>
                <td className="n">{Number.isNaN(sum.first.countPct) ? '–' : `${Math.round(sum.first.countPct)}%`}</td>
                <td className="n">{sum.last ? (Number.isNaN(sum.last.countPct) ? '–' : `${Math.round(sum.last.countPct)}%`) : ''}</td>
              </tr>
            </tbody>
          </table>
          {props.sessionMiss ? (
            <div className="muted">
              Biggest miss: {props.sessionMiss.phase}, {props.sessionMiss.gap.toFixed(1)} pt — you {props.sessionMiss.yourPlay}; better: {props.sessionMiss.bestPlay}.
            </div>
          ) : null}
          {props.tiers ? (
            <div className="muted">
              Coach level: {TIER_NAME[props.tiers[0]]}
              {props.tiers[0] !== props.tiers[1] ? ` → ${TIER_NAME[props.tiers[1]]}` : ''}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
